import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ModuleHeader } from '@/design-system/dna';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, Sprout } from 'lucide-react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import VisitFlowCard from "./VisitFlowCard";
import WelcomeCard from "./WelcomeCard";
import SchoolOriginsManager from "./SchoolOriginsManager";
import { ageFromISO, fmtBR, seriesAgeCheck } from "@/lib/seriesAge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "novo_contato",       label: "Novos",              color: "#7B3F98", bg: "#f5f0ff" },
  { key: "em_atendimento",     label: "Em atendimento",     color: "#0ea5e9", bg: "#eef8fe" },
  { key: "aguardando_confirmacao", label: "Aguardando confirmação", color: "#eab308", bg: "#fefce8" },
  { key: "visita_agendada",    label: "Visitas agendadas",  color: "#f59e0b", bg: "#fff8ec" },
  { key: "reagendada",         label: "Reagendada",         color: "#f97316", bg: "#fff7ed" },
  { key: "visita_realizada",   label: "Visitaram",          color: "#8E52AB", bg: "#f5f3ff" },
  { key: "aguardando_decisao", label: "Aguardando",         color: "#ef4444", bg: "#fef2f2" },
  { key: "matricula_iniciada", label: "Matrícula Iniciada", color: "#8E52AB", bg: "#f5f3ff" },
  { key: "matriculado",        label: "Convertidos",        color: "#10b981", bg: "#ecfdf5" },
  { key: "perdido",            label: "Perdidos",           color: "#94a3b8", bg: "#f1f5f9" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

const KPI_STATUSES = ["novo_contato", "em_atendimento", "visita_agendada", "aguardando_confirmacao", "matriculado", "perdido"];
const FUNNEL_ORDER = KPI_STATUSES;
const STAGE_LABEL = { novo_contato: "Novos", em_atendimento: "Atendimento", visita_agendada: "Visita",
  aguardando_confirmacao: "Aguardando confirmação", matriculado: "Convertidos", perdido: "Perdidos" };
const pctOf = (v, t) => (t ? (v / t * 100).toFixed(2).replace(".", ",") : "0");

const LEAD_SOURCE_LABELS = {
  familia_aluno: "Família/aluno da escola",
  amigo_conhecido: "Amigo ou conhecido",
  instagram: "Instagram",
  facebook: "Facebook",
  google: "Google",
  passou_frente: "Passou em frente à escola",
  evento: "Evento",
  professor_colaborador: "Professor/colaborador",
  outro: "Outro",
};
const sourceLabel = (p) => LEAD_SOURCE_LABELS[p?.lead_source] || p?.origin_other || "—";

function token() { return localStorage.getItem("token"); }
function authHeaders() { return { Authorization: `Bearer ${token()}` }; }

function seriesAgeWarning(child, year) {
  const c = seriesAgeCheck({ birthDate: child?.birth_date, seriesLabel: child?.interest_series,
                             targetYear: child?.interest_year || year });
  return c && c.level === "warn" ? c.message : null;
}

/** Filhos do lead — sempre lista (leads antigos têm 1 filho nos campos de topo). */
function childrenOf(p) {
  if (Array.isArray(p?.students) && p.students.length) return p.students;
  if (!p?.student_name) return [];
  return [{
    id: null, name: p.student_name, birth_date: p.student_birth_date,
    interest_series: p.interest_series, interest_year: p.interest_year,
    student_period: p.student_period, current_school: p.current_school,
    wants_integral: p.wants_integral, wants_lunch: p.wants_lunch,
    student_id: p.converted_student_id, enrollment_process_id: p.enrollment_process_id,
    status: p.converted_student_id ? "matricula_iniciada" : "nao_iniciado",
    birth_date_conflict: p.birth_date_conflict,
  }];
}

export default function NovosDNAsPanel() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [funnelTotal, setFunnelTotal] = useState(0);
  const [insights, setInsights] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [filterOptions, setFilterOptions] = useState({ lead_sources: [], current_schools: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pageInfo, setPageInfo] = useState({ total: 0, total_pages: 1 });
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [schoolsOpen, setSchoolsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stale, setStale] = useState({ count: 0, leads: [] });
  const [report, setReport] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.stage = statusFilter;
      if (sourceFilter) params.lead_source = sourceFilter;
      if (schoolFilter) params.school_normalized = schoolFilter;
      if (q) params.q = q;
      params.page = page;
      params.page_size = pageSize;
      const [{ data: list }, { data: ins }] = await Promise.all([
        axios.get(`${API}/prospects`, { params, headers: authHeaders() }),
        axios.get(`${API}/prospects/insights/summary`, { headers: authHeaders() }),
      ]);
      setProspects(list.prospects || []);
      setBuckets(list.buckets || {});
      setFunnelTotal(list.total_unique || 0);
      setFilterOptions(list.filter_options || { lead_sources: [], current_schools: [] });
      setPageInfo({ total: list.total ?? 0, total_pages: list.total_pages ?? 1 });
      setInsights(ins);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar leads");
    } finally { setLoading(false); }
  }, [statusFilter, q, sourceFilter, schoolFilter, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  // busca instantânea (sem reload): debounce curto no que o usuário digita
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 280);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => { setPage(1); }, [statusFilter, q, sourceFilter, schoolFilter, pageSize]);

  const loadStale = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/prospects/alerts/stale`, { params: { days: 3 }, headers: authHeaders() });
      setStale(data);
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => { loadStale(); }, [loadStale]);

  const loadReport = useCallback(async (ref) => {
    try {
      const { data } = await axios.get(`${API}/prospects/reports/monthly`, { params: { month: ref }, headers: authHeaders() });
      setReport(data);
    } catch (err) { toast.error("Erro ao carregar relatório mensal"); }
  }, []);
  useEffect(() => { loadReport(month); }, [month, loadReport]);

  const openDetail = async (pid) => {
    try {
      const { data } = await axios.get(`${API}/prospects/${pid}`, { headers: authHeaders() });
      setSelected(data);
    } catch (err) { toast.error("Erro ao abrir lead"); }
  };

  const patchProspect = async (pid, body) => {
    try {
      const { data } = await axios.patch(`${API}/prospects/${pid}`, body, { headers: authHeaders() });
      setSelected(data);
      load();
      loadStale();
      toast.success("Lead atualizado");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const convertToEnrollment = async (pid, leadStudentIds) => {
    const lead = prospects.find((x) => x.id === pid) || selected || {};
    const kids = childrenOf(lead);
    const count = leadStudentIds?.length || kids.length || 1;
    if (!lead.enrollment_process_id
        && !window.confirm(`Iniciar matrícula de ${count} aluno(s) desta família? Criaremos os alunos e o responsável (um só) e abriremos um processo por aluno no DNA Matrículas (sem criar contrato).`)) return;
    try {
      const { data } = await axios.post(`${API}/prospects/${pid}/convert-to-enrollment`,
        leadStudentIds?.length ? { lead_student_ids: leadStudentIds } : {},
        { headers: authHeaders() });
      if (data.warning) toast.warning(data.warning);
      else toast.success(data.already
        ? `Processo já aberto em ${data.campaign_name || 'DNA Matrículas'} — abrindo`
        : `${data.students_count || 1} matrícula(s) nova(s) aberta(s) em ${data.campaign_name || 'DNA Matrículas'}`);
      setSelected(null); load();
      const conflict = (data.students || []).find((s) => s.birth_date_conflict);
      if (conflict) {
        toast.warning(`${conflict.name}: divergência na data de nascimento — existente ${fmtBR(conflict.birth_date_conflict.existing)} · Novos DNAs ${fmtBR(conflict.birth_date_conflict.incoming)}. Escolha no lead antes de seguir.`,
          { duration: 10000 });
        return;
      }
      setTimeout(() => navigate("/v2/matriculas/painel"), 900);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const total = prospects.length;
  const visitasHoje = useMemo(() => {
    const today = new Date().toDateString();
    return prospects.filter((p) => p.visit_scheduled_at && new Date(p.visit_scheduled_at).toDateString() === today);
  }, [prospects]);
  const ultimasFamilias = prospects;

  // Funil = MESMA fonte dos cards (status atual único por família; soma = total)
  const funil = useMemo(() => FUNNEL_ORDER.map((k) => ({
    key: k, label: STAGE_LABEL[k], value: buckets[k] || 0,
    pct: pctOf(buckets[k] || 0, funnelTotal), color: STATUS_MAP[k].color,
  })), [buckets, funnelTotal]);

  // Origem dos contatos (agrupado por origin)
  const origens = useMemo(() => {
    const map = {};
    prospects.forEach((p) => { const o = p.origin || "outro"; map[o] = (map[o] || 0) + 1; });
    const labels = { public_form: "Site (formulário)", referral: "Indicação", social: "Redes sociais", other: "Outros", outro: "Outros" };
    const colors = ["#7B3F98", "#ec4899", "#0ea5e9", "#f59e0b", "#10b981"];
    return Object.entries(map).map(([k, v], i) => ({
      label: labels[k] || k, value: v, color: colors[i % colors.length],
      pct: total ? Math.round((v / total) * 100) : 0,
    }));
  }, [prospects, total]);

  return (
    <div style={S.page} data-testid="novosdna-panel">
      {/* HEADER */}
      <div style={{ marginBottom: 20 }}>
        <ModuleHeader kicker="Secretaria · Novos DNAs" title="Novos DNAs" icon={Sprout} testid="novosdna-header"
          subtitle="Acompanhe seus leads, converta e visite agendadas"
          actions={<>
            <Button variant="outline" onClick={load} data-testid="novosdna-refresh"><RefreshCw className="h-4 w-4" /> Atualizar</Button>
            <Button onClick={() => window.open("/conheca-o-novo-dna", "_blank")} className="dna-mh-primary" data-testid="novosdna-new-campaign"><Plus className="h-4 w-4" /> Nova campanha</Button>
          </>} />
      </div>

      {/* KPI Grid grande */}
      <div style={S.kpiGrid}>
        {KPI_STATUSES.map((k) => {
          const st = STATUS_MAP[k];
          const val = buckets[k] || 0;
          return (
            <div key={k} onClick={() => setStatusFilter(statusFilter === k ? "" : k)}
              style={{ ...S.kpiCard, borderTop: `3px solid ${st.color}`, ...(statusFilter === k ? { boxShadow: `0 0 0 2px ${st.color}` } : {}) }}
              data-testid={`novosdna-kpi-${k}`}>
              <div style={{ ...S.kpiLabel, color: st.color }}>{STAGE_LABEL[k]}</div>
              <div style={S.kpiValue}>{val}</div>
              <div style={S.kpiFoot}>
                {funnelTotal ? `${pctOf(val, funnelTotal)}% de ${funnelTotal} famílias` : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* ALERTA — LEADS PARADOS (>3 dias sem contato) */}
      {stale.count > 0 && (
        <div style={S.alertBox} data-testid="novosdna-stale-alert">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#b91c1c", letterSpacing: 0.6 }}>
                ⚠ LEADS PARADOS · SEM CONTATO HÁ 3+ DIAS
              </div>
              <div style={{ fontSize: 14, color: "#7f1d1d", marginTop: 2 }}>
                <b>{stale.count}</b> {stale.count === 1 ? "família aguarda" : "famílias aguardam"} retorno da secretaria.
              </div>
            </div>
            <button onClick={loadStale} style={S.btnGhost} data-testid="novosdna-stale-refresh">↻ Revisar</button>
          </div>
          <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
            {stale.leads.slice(0, 5).map((l) => (
              <div key={l.id} style={S.staleRow} data-testid={`novosdna-stale-${l.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{l.student_name}</div>
                  <div style={{ fontSize: 12, color: "#7f1d1d" }}>
                    Família {(l.responsible_name || "").split(" ")[0]} · {l.interest_series || "—"} · {STATUS_MAP[l.status]?.label || l.status}
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#b91c1c", whiteSpace: "nowrap" }}>
                  {l.days_idle} dias
                </div>
                <a href={`https://wa.me/55${(l.responsible_phone_norm || "").replace(/^55/, "")}?text=${encodeURIComponent(`Olá ${(l.responsible_name || "").split(" ")[0]}, tudo bem? Aqui é da secretaria do Novo DNA. Ainda podemos agendar sua visita?`)}`}
                  target="_blank" rel="noreferrer" data-testid={`novosdna-stale-wa-${l.id}`}
                  style={{ ...S.btnAction("#25D366"), textDecoration: "none", fontSize: 12, padding: "7px 12px" }}>
                  WhatsApp
                </a>
                <button onClick={() => openDetail(l.id)} data-testid={`novosdna-stale-open-${l.id}`}
                  style={{ ...S.btnAction("#7B3F98"), fontSize: 12, padding: "7px 12px" }}>
                  Abrir
                </button>
              </div>
            ))}
            {stale.count > 5 && (
              <div style={{ fontSize: 12, color: "#7f1d1d" }}>+ {stale.count - 5} outras famílias paradas</div>
            )}
          </div>
        </div>
      )}

      {/* GRID PRINCIPAL: coluna esquerda (visitas + últimas) + direita (charts + actions) */}
      <div style={S.mainGrid} className="nd-main-grid">
        {/* COLUNA ESQUERDA */}
        <div style={{ display: "grid", gap: 16 }}>
          {/* Visitas de hoje */}
          <div style={S.panel}>
            <div style={S.panelHead}>
              <div style={S.panelTitle}>📅 Visitas de hoje</div>
              <div style={S.panelBadge}>{visitasHoje.length}</div>
            </div>
            {visitasHoje.length === 0 ? (
              <div style={S.empty}>Nenhuma visita agendada para hoje.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {visitasHoje.map((p) => (
                  <div key={p.id} onClick={() => openDetail(p.id)} style={S.visitRow} data-testid={`novosdna-visit-${p.id}`}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.student_name}</div>
                      <div style={{ fontSize: 12, color: "#6b6483" }}>Família {p.responsible_name.split(" ")[0]} · {p.interest_series || "—"}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>
                      {new Date(p.visit_scheduled_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Últimas famílias */}
          <div style={S.panel}>
            <div style={S.panelHead}>
              <div style={S.panelTitle}>👨‍👩‍👧 Famílias</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                  style={S.searchInput} data-testid="novosdna-filter-source">
                  <option value="">Todas as origens</option>
                  {filterOptions.lead_sources.map((s) => (
                    <option key={s} value={s}>{LEAD_SOURCE_LABELS[s] || s}</option>
                  ))}
                </select>
                <select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}
                  style={S.searchInput} data-testid="novosdna-filter-school">
                  <option value="">Todas as escolas de origem</option>
                  {(filterOptions.schools_normalized || filterOptions.current_schools).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input placeholder="Buscar aluno, responsável, escola ou família..."
                  value={qInput} onChange={(e) => setQInput(e.target.value)}
                  style={{ ...S.searchInput, width: 260 }} data-testid="novosdna-search" />
                <button onClick={() => setSchoolsOpen(true)} style={S.manageBtn}
                  data-testid="novosdna-manage-schools">Gerenciar escolas de origem</button>
              </div>
            </div>
            {loading ? (
              <div style={S.empty}>Carregando...</div>
            ) : ultimasFamilias.length === 0 ? (
              <div style={S.empty}>
                Nenhum lead ainda. Compartilhe{" "}
                <code style={{ background: "#f5f0ff", padding: "1px 6px", borderRadius: 4 }}>/conheca-o-novo-dna</code>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={S.table} data-testid="novosdna-latest-table">
                  <thead>
                    <tr>
                      <th style={S.th}>Família / Filhos</th>
                      <th style={S.th}>Responsável</th>
                      <th style={S.th}>Série(s)</th>
                      <th style={S.th}>Escola de origem</th>
                      <th style={S.th}>Origem</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Data</th>
                      <th style={S.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimasFamilias.map((p) => {
                      const st = STATUS_MAP[p.funnel_stage === "matriculado" ? "matriculado" : p.status] || STATUS_MAP.novo_contato;
                      const kids = childrenOf(p);
                      return (
                        <tr key={p.id} style={S.tr} onClick={() => openDetail(p.id)} data-testid={`novosdna-row-${p.id}`}>
                          <td style={S.td}>
                            <b>Família {(p.responsible_name || "").split(" ")[0]}</b>
                            {kids.length > 1 && (
                              <span style={{ ...S.chip, background: "#f5f0ff", color: "#64327D", marginLeft: 6 }}
                                data-testid={`novosdna-row-siblings-${p.id}`}>
                                {kids.length} filhos
                              </span>
                            )}
                            <div style={{ fontSize: 12, color: "#6b6483", marginTop: 2 }}>
                              {kids.map((c) => `${c.name} — ${c.interest_series || "—"}`).join(" · ") || "—"}
                            </div>
                          </td>
                          <td style={S.td}>{p.responsible_name}</td>
                          <td style={S.td}>{kids.map((c) => c.interest_series || "—").join(", ") || "—"}</td>
                          <td style={S.td}>
                            {p.school_origin_normalized || p.current_school || "—"}
                            {p.school_origin_raw && p.school_origin_normalized
                              && p.school_origin_raw !== p.school_origin_normalized && (
                              <div style={{ fontSize: 11, color: "#8b82a6" }}>{p.school_origin_raw}</div>
                            )}
                          </td>
                          <td style={S.td}>{sourceLabel(p)}</td>
                          <td style={S.td}>
                            <span style={{ ...S.chip, background: st.bg, color: st.color }}>{st.label}</span>
                          </td>
                          <td style={S.td}>{p.created_at ? new Date(p.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                          <td style={S.td}>→</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={S.pagBar} data-testid="novosdna-pagination">
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Exibindo {pageInfo.total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, pageInfo.total)} de {pageInfo.total} registros
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                      style={{ ...S.searchInput, padding: "6px 8px", fontSize: 12 }} data-testid="novosdna-page-size">
                      <option value={20}>20 / página</option>
                      <option value={50}>50 / página</option>
                      <option value={100}>100 / página</option>
                    </select>
                    <button onClick={() => setPage((v) => Math.max(1, v - 1))} disabled={page <= 1}
                      style={{ ...S.pagBtn, opacity: page <= 1 ? 0.4 : 1 }} data-testid="novosdna-prev-page">← Anterior</button>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#3F1F5C" }} data-testid="novosdna-page-info">
                      {page} / {pageInfo.total_pages}
                    </span>
                    <button onClick={() => setPage((v) => Math.min(pageInfo.total_pages, v + 1))} disabled={page >= pageInfo.total_pages}
                      style={{ ...S.pagBtn, opacity: page >= pageInfo.total_pages ? 0.4 : 1 }} data-testid="novosdna-next-page">Próxima →</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* COLUNA DIREITA */}
        <div style={{ display: "grid", gap: 16 }}>
          {/* Funil de conversão */}
          <div style={S.panel}>
            <div style={S.panelHead}><div style={S.panelTitle}>🎯 Funil de conversão</div></div>
            <Donut data={funil.map((f) => ({ ...f, value: Math.max(f.value, 0) }))} size={160} />
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {funil.map((f) => (
                <div key={f.key} data-testid={`novosdna-funnel-${f.key}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: f.color }} />
                  <span style={{ flex: 1, color: "#3f2a6b" }}>{f.label}</span>
                  <b>{f.value}</b>
                  <span style={{ color: "#94a3b8", fontSize: 11, width: 48, textAlign: "right" }}>{f.pct}%</span>
                </div>
              ))}
              <div data-testid="novosdna-funnel-total" style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #eee", paddingTop: 6, fontSize: 12, color: "#64748b" }}>
                <span>Total de famílias únicas</span><b>{funnelTotal}</b>
              </div>
            </div>
          </div>

          {/* Origem dos contatos */}
          <div style={S.panel}>
            <div style={S.panelHead}><div style={S.panelTitle}>📊 Origem dos contatos</div></div>
            {origens.length === 0 ? (
              <div style={S.empty}>Sem dados ainda</div>
            ) : (
              <>
                <Donut data={origens} size={160} />
                <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
                  {origens.map((o) => (
                    <div key={o.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: o.color }} />
                      <span style={{ flex: 1, color: "#3f2a6b" }}>{o.label}</span>
                      <b>{o.value}</b>
                      <span style={{ color: "#94a3b8", fontSize: 11, width: 36, textAlign: "right" }}>{o.pct}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Insight extra */}
          {insights && (
            <div style={S.insightBox}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#7B3F98", letterSpacing: 0.6 }}>💡 INSIGHT</div>
              <div style={{ fontSize: 14, color: "#1c1230", marginTop: 4, lineHeight: 1.4 }}>
                <b>{insights.top_series?._id || "—"}</b> é a série mais procurada.{" "}
                <b>{insights.integral_percent}%</b> das famílias querem período integral.
                {insights.novos_sem_contato_24h > 0 && (
                  <> Você tem <b style={{ color: "#ef4444" }}>{insights.novos_sem_contato_24h}</b> leads sem contato há mais de 24h.</>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RELATÓRIO MENSAL */}
      <div style={{ ...S.panel, marginTop: 16 }} data-testid="novosdna-monthly-report">
        <div style={S.panelHead}>
          <div style={S.panelTitle}>📈 Relatório mensal · Novos DNAs</div>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            style={S.searchInput} data-testid="novosdna-report-month" />
        </div>
        {!report ? (
          <div style={S.empty}>Carregando relatório...</div>
        ) : (
          <>
            <div style={S.reportKpis} className="nd-report-kpis">
              <ReportKpi label="Novos contatos" value={report.novos_contatos} color="#7B3F98" testId="report-kpi-contatos" />
              <ReportKpi label="Visitas" value={report.visitas} color="#f59e0b" testId="report-kpi-visitas" />
              <ReportKpi label="Matrículas" value={report.matriculas} color="#10b981" testId="report-kpi-matriculas" />
              <ReportKpi label="Conversão" value={`${report.conversao_percent}%`} color="#0ea5e9" testId="report-kpi-conversao" />
            </div>
            <div style={S.reportGrid}>
              <RankList title="Origem dos contatos" rows={report.por_origem} labelFn={(k) => LEAD_SOURCE_LABELS[k] || k}
                color="#7B3F98" testId="novosdna-report-origem" empty="Sem contatos neste mês" />
              <RankList title="Maiores indicadores" rows={report.maiores_indicadores} medals
                color="#ec4899" testId="novosdna-report-indicadores" empty="Nenhuma indicação neste mês" />
              <RankList title="Escolas de origem" rows={report.escolas_origem}
                color="#0ea5e9" testId="novosdna-report-escolas" empty="Sem escolas informadas"
                onPick={(name) => {
                  setSchoolFilter(name);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                action={(
                  <button onClick={() => setSchoolsOpen(true)} style={S.link}
                    data-testid="novosdna-manage-schools-report">Gerenciar</button>
                )} />
            </div>
          </>
        )}
      </div>

      {schoolsOpen && (
        <SchoolOriginsManager onClose={() => setSchoolsOpen(false)}
          onChanged={() => { load(); loadReport(month); }} />
      )}

      {selected && (
        <LeadDrawer
          prospect={selected}
          onClose={() => setSelected(null)}
          onPatch={(body) => patchProspect(selected.id, body)}
          onConvert={(ids) => convertToEnrollment(selected.id, ids)}
          onLocalUpdate={(fresh) => { setSelected(fresh); load(); loadStale(); }}
        />
      )}
    </div>
  );
}

/* ============ Donut Chart (SVG puro) ============ */
function Donut({ data, size = 160 }) {
  const total = data.reduce((a, b) => a + (b.value || 0), 0) || 1;
  const R = size / 2, r = R - 22, cx = R, cy = R;
  let acc = 0;
  const arcs = data.map((d) => {
    const v = d.value || 0;
    const a1 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    acc += v;
    const a2 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const large = a2 - a1 > Math.PI ? 1 : 0;
    const x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1);
    const x2 = cx + R * Math.cos(a2), y2 = cy + R * Math.sin(a2);
    const x3 = cx + r * Math.cos(a2), y3 = cy + r * Math.sin(a2);
    const x4 = cx + r * Math.cos(a1), y4 = cy + r * Math.sin(a1);
    if (v === 0) return null;
    return (
      <path key={d.label} d={`M${x1},${y1} A${R},${R} 0 ${large} 1 ${x2},${y2} L${x3},${y3} A${r},${r} 0 ${large} 0 ${x4},${y4} Z`}
        fill={d.color} />
    );
  });
  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={R} fill="#f5f0ff" />
        {arcs}
        <circle cx={cx} cy={cy} r={r - 3} fill="#fff" />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={22} fontWeight={800} fill="#1c1230">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize={11} fill="#94a3b8">total</text>
      </svg>
    </div>
  );
}

/* ============ Relatório: KPI + Ranking ============ */
function ReportKpi({ label, value, color, testId }) {
  return (
    <div style={{ ...S.reportKpi, borderLeft: `3px solid ${color}` }} data-testid={testId}>
      <div style={{ fontSize: 11, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

const MEDALS = ["🥇", "🥈", "🥉"];

function RankList({ title, rows = [], labelFn, color, testId, empty, medals, onPick, action }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={S.rankBox} data-testid={testId}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1c1230" }}>{title}</div>
        {action}
      </div>
      {rows.length === 0 ? (
        <div style={{ ...S.empty, padding: 14 }}>{empty}</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((r, i) => (
            <div key={r._id} onClick={onPick ? () => onPick(r._id) : undefined}
              style={onPick ? { cursor: "pointer" } : undefined}
              data-testid={onPick ? `${testId}-row-${i}` : undefined}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                {medals && <span style={{ width: 18 }}>{MEDALS[i] || ""}</span>}
                <span style={{ flex: 1, minWidth: 0, color: "#3f2a6b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {labelFn ? labelFn(r._id) : r._id}
                </span>
                <b>{r.count}</b>
              </div>
              <div style={{ height: 5, background: "#f1ecfa", borderRadius: 999, marginTop: 4 }}>
                <div style={{ width: `${(r.count / max) * 100}%`, height: "100%", background: color, borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Drawer ============ */
function LeadDrawer({ prospect, onClose, onPatch, onConvert, onLocalUpdate }) {
  const [note, setNote] = useState("");
  const [waMsg, setWaMsg] = useState("");
  const [resolving, setResolving] = useState(false);
  const p = prospect;
  const kids = childrenOf(p);
  const [pickedKids, setPickedKids] = useState([]);
  useEffect(() => {
    setPickedKids(childrenOf(prospect).filter((c) => !c.student_id).map((c) => c.id).filter(Boolean));
  }, [prospect]);
  const st = STATUS_MAP[p.funnel_stage === "matriculado" ? "matriculado" : p.status] || STATUS_MAP.novo_contato;
  const waPhone = (p.responsible_phone_norm || "").replace(/^55/, "");
  const waUrl = waMsg
    ? `https://wa.me/55${waPhone}?text=${encodeURIComponent(waMsg)}`
    : `https://wa.me/55${waPhone}`;

  const handleStatus = (s) => { if (s !== p.status) onPatch({ status: s }); };
  const handleNote = () => { if (!note.trim()) return; onPatch({ note: note.trim() }); setNote(""); };

  const resolveBirth = async (choice, leadStudentId) => {
    setResolving(true);
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/birth-date/resolve`,
        { choice, lead_student_id: leadStudentId || null }, { headers: authHeaders() });
      toast.success(`Data de nascimento definida: ${fmtBR(data.birth_date)}`);
      onLocalUpdate?.({ ...p, birth_date_conflict: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível resolver a divergência.");
    } finally { setResolving(false); }
  };

  return (
    <div onClick={onClose} style={S.drawerOverlay}>
      <div onClick={(e) => e.stopPropagation()} style={S.drawer} data-testid="novosdna-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#7B3F98", letterSpacing: 0.6 }}>
              PROTOCOLO {p.protocol || p.id.slice(0, 8).toUpperCase()}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: "4px 0 0" }}>
              Família {(p.responsible_name || "").split(" ")[0]}
            </h2>
            <div style={{ fontSize: 13, color: "#6b6483", marginTop: 2 }}>
              {kids.length} filho(s) interessado(s) · {kids.map((c) => c.name).join(", ") || "—"} · {p.interest_year || "—"}
            </div>
          </div>
          <button onClick={onClose} style={S.drawerClose} data-testid="novosdna-drawer-close">×</button>
        </div>

        {/* Status */}
        <SectionTitle icon="⚙️">Status atual</SectionTitle>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => handleStatus(s.key)}
              data-testid={`novosdna-status-${s.key}`}
              style={{
                padding: "5px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
                border: `1.5px solid ${p.status === s.key ? s.color : "#e5e0f0"}`,
                background: p.status === s.key ? s.color : s.bg, color: p.status === s.key ? "#fff" : s.color,
              }}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Contato */}
        <SectionTitle icon="📞" style={{ marginTop: 22 }}>Contato do responsável</SectionTitle>
        <div style={S.infoBox}>
          <div>👤 <b>{p.responsible_name}</b></div>
          <div style={{ marginTop: 4 }}>📞 {p.responsible_phone}</div>
          <div style={{ marginTop: 4 }}>✉️ {p.responsible_email}</div>
        </div>

        {/* Filhos interessados */}
        <SectionTitle icon="🎓" style={{ marginTop: 22 }}>
          Filhos interessados ({kids.length})
        </SectionTitle>
        <div style={{ display: "grid", gap: 8, marginTop: 8 }} data-testid="novosdna-drawer-student-info">
          {kids.map((c, i) => {
            const warn = seriesAgeWarning(c, p.interest_year);
            const conflict = c.birth_date_conflict;
            return (
              <div key={c.id || `${c.name}-${i}`} style={S.infoBox}
                data-testid={`novosdna-drawer-child-${i}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{c.name || "—"}</div>
                  <span style={{ ...S.chip, ...(c.student_id
                    ? { background: "#ecfdf5", color: "#047857" }
                    : { background: "#f1f5f9", color: "#475569" }) }}
                    data-testid={`novosdna-child-status-${i}`}>
                    {c.student_id ? "Matrícula iniciada" : "Não iniciado"}
                  </span>
                </div>
                <div style={{ marginTop: 4 }} data-testid={`novosdna-child-birth-${i}`}>
                  Nascimento: <b>{fmtBR(c.birth_date)}</b>
                  {ageFromISO(c.birth_date) !== null && (
                    <> · Idade: <b>{ageFromISO(c.birth_date)} anos</b></>
                  )}
                </div>
                <div style={{ marginTop: 4 }}>Série pretendida: <b>{c.interest_series || "—"}</b></div>
                <div style={{ marginTop: 4 }}>Período: <b>{c.student_period || "—"}</b></div>
                <div style={{ marginTop: 4 }}>Escola atual: <b>{c.current_school || "—"}</b></div>
                <div style={{ marginTop: 4 }}>
                  ⏰ Integral: <b>{c.wants_integral ? "Sim" : "Não"}</b> · 🍽 Almoço: <b>{c.wants_lunch ? "Sim" : "Não"}</b>
                </div>
                {c.observation && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#5a5478" }}>💬 {c.observation}</div>
                )}
                {warn && (
                  <div data-testid={`novosdna-child-age-warning-${i}`}
                    style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "#fefce8",
                              border: "1px solid #fde68a", color: "#92400e", fontSize: 12 }}>
                    ⚠️ {warn}
                  </div>
                )}
                {conflict && (
                  <div data-testid={`novosdna-birth-conflict-${i}`}
                    style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "#fef2f2",
                              border: "1px solid #fecaca" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: "#b91c1c" }}>
                      Divergência na data de nascimento
                    </div>
                    <div style={{ fontSize: 12.5, marginTop: 4, color: "#7f1d1d" }}>
                      Data existente: <b>{fmtBR(conflict.existing)}</b><br />
                      Novos DNAs: <b>{fmtBR(conflict.incoming)}</b>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                      <button onClick={() => resolveBirth("existing", c.id)} disabled={resolving}
                        data-testid={`novosdna-birth-keep-existing-${i}`}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #b91c1c",
                                  background: "#fff", color: "#b91c1c", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                        Manter {fmtBR(conflict.existing)}
                      </button>
                      <button onClick={() => resolveBirth("lead", c.id)} disabled={resolving}
                        data-testid={`novosdna-birth-use-lead-${i}`}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #b91c1c",
                                  background: "#b91c1c", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                        Usar {fmtBR(conflict.incoming)}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div style={S.infoBox}>
            <div>Origem: <b>{sourceLabel(p)}</b></div>
            {p.referral_name && (
              <div style={{ marginTop: 4 }} data-testid="novosdna-drawer-referral">
                Indicado por: <b>{p.referral_name}</b>
              </div>
            )}
          </div>
        </div>

        {/* Preferências */}
        <SectionTitle icon="⭐" style={{ marginTop: 22 }}>Preferências da família</SectionTitle>
        <div style={S.infoBox}>
          <div>📅 Visita preferida: <b>{p.preferred_visit_period || "—"}</b></div>
          {p.observation && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #e5e0f0" }}>💬 {p.observation}</div>
          )}
        </div>

        {/* AGENDAR VISITA + CONFIRMAÇÃO + WHATSAPP (mimicando o design da referência) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginTop: 22 }}>
          <WelcomeCard prospect={p} onUpdated={onLocalUpdate} styles={S} />

          <VisitFlowCard prospect={p} onUpdated={onLocalUpdate} styles={S} />

          <div style={S.actionCard}>
            <div style={S.actionCardHead}>💬 Enviar mensagem (WhatsApp)</div>
            <textarea value={waMsg} onChange={(e) => setWaMsg(e.target.value)}
              placeholder={`Olá ${p.responsible_name.split(" ")[0]}, tudo bem? Aqui é da secretaria do Novo DNA...`}
              data-testid="novosdna-wa-msg"
              style={{ ...S.input, minHeight: 60, resize: "vertical" }} />
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="novosdna-drawer-wa"
              style={{ ...S.btnAction("#25D366"), marginTop: 8, display: "inline-block", textDecoration: "none", textAlign: "center" }}>
              Abrir WhatsApp
            </a>
          </div>
        </div>

        {/* Nota */}
        <SectionTitle icon="📝" style={{ marginTop: 22 }}>Adicionar nota</SectionTitle>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
          data-testid="novosdna-note-input" placeholder="Ex.: Ligou pedindo informações sobre o material do 6º ano..."
          style={{ ...S.input, minHeight: 60, resize: "vertical", marginTop: 6 }} />
        <button onClick={handleNote} disabled={!note.trim()} data-testid="novosdna-note-save"
          style={{ ...S.btnAction("#7B3F98"), marginTop: 6, opacity: note.trim() ? 1 : 0.5 }}>
          Salvar nota
        </button>

        {/* Timeline */}
        <SectionTitle icon="🕘" style={{ marginTop: 22 }}>Histórico</SectionTitle>
        <div style={{ marginTop: 8, borderLeft: "2px solid #ede7f7", paddingLeft: 14 }}>
          {(p.timeline || []).slice().reverse().map((ev, i) => (
            <div key={i} style={{ position: "relative", marginBottom: 12 }}>
              <div style={{ position: "absolute", left: -20, top: 5, width: 8, height: 8, borderRadius: 999, background: "#7B3F98" }} />
              <div style={{ fontSize: 12, color: "#6b6483" }}>
                {ev.at ? new Date(ev.at).toLocaleString("pt-BR") : ""}{ev.by ? ` · ${ev.by}` : ""}
              </div>
              <div style={{ fontSize: 13, marginTop: 1 }}><b>{ev.event}</b>{ev.note ? ` — ${ev.note}` : ""}</div>
            </div>
          ))}
        </div>

        {/* Converter */}
        <div style={{ ...S.convertBox, marginTop: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#3F1F5C" }}>
            {p.enrollment_process_id ? "📂 Matrícula em andamento" : "🚀 Pronto para virar matrícula?"}
          </div>
          <div style={{ fontSize: 12, color: "#5a5478", marginTop: 4, marginBottom: 10 }}>
            {p.enrollment_process_id
              ? "Processo aberto no DNA Matrículas como MATRÍCULA NOVA. Nenhum contrato é criado nesta etapa."
              : "Criaremos o aluno + responsável (sem duplicar) e abriremos o processo no DNA Matrículas como matrícula nova."}
          </div>
          {kids.length > 1 && (
            <div style={{ marginBottom: 10 }} data-testid="novosdna-convert-picker">
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#3F1F5C", marginBottom: 6 }}>
                Quais alunos deseja iniciar?
              </div>
              {kids.map((c, i) => (
                <label key={c.id || i} style={{ display: "flex", alignItems: "center", gap: 8,
                          fontSize: 13, padding: "3px 0", cursor: c.student_id ? "default" : "pointer" }}>
                  <input type="checkbox" disabled={!c.id || !!c.student_id}
                    checked={pickedKids.includes(c.id)}
                    onChange={(e) => setPickedKids((list) => e.target.checked
                      ? [...list, c.id] : list.filter((x) => x !== c.id))}
                    data-testid={`novosdna-convert-pick-${i}`} />
                  <span>
                    {c.name} · {c.interest_series || "série a definir"}
                    {c.student_id && <b style={{ color: "#047857" }}> · já iniciado</b>}
                  </span>
                </label>
              ))}
            </div>
          )}
          <button onClick={() => onConvert(kids.length > 1 ? pickedKids : null)}
            disabled={kids.length > 1 && pickedKids.length === 0}
            data-testid="novosdna-convert-btn"
            style={{ ...S.btnAction("#7B3F98"), width: "100%", padding: "11px 16px", fontSize: 15,
                      opacity: kids.length > 1 && pickedKids.length === 0 ? 0.5 : 1 }}>
            {kids.length > 1
              ? `Iniciar matrículas (${pickedKids.length}) →`
              : (p.enrollment_process_id ? "Abrir Matrícula →" : "Iniciar Matrícula →")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, children, style }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, color: "#7B3F98", textTransform: "uppercase", letterSpacing: 0.8, ...style }}>
      {icon} {children}
    </div>
  );
}

/* ============ Styles ============ */
const S = {
  page: { padding: 24, fontFamily: "'Inter', system-ui, sans-serif", maxWidth: 1400, margin: "0 auto", color: "#1c1230" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" },
  headerKicker: { fontSize: 11, fontWeight: 800, color: "#7B3F98", letterSpacing: 1 },
  headerTitle: { fontSize: 28, fontWeight: 800, margin: "2px 0 0", letterSpacing: -0.5 },
  headerSub: { fontSize: 13, color: "#6b6483", marginTop: 2 },
  btnPrimary: { padding: "10px 18px", background: "linear-gradient(90deg, #7B3F98, #ec4899)", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 16px rgba(124,58,237,0.25)" },
  btnGhost: { padding: "10px 16px", background: "#fff", color: "#7B3F98", border: "1.5px solid #ddd0f9", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 20 },
  kpiCard: { background: "#fff", padding: "18px 18px 16px", borderRadius: 14, border: "1px solid #ede7f7", cursor: "pointer", transition: "all .12s", boxShadow: "0 2px 8px rgba(76,29,149,0.04)" },
  kpiLabel: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 },
  kpiValue: { fontSize: 34, fontWeight: 900, marginTop: 6, lineHeight: 1, letterSpacing: -1 },
  kpiFoot: { fontSize: 11, color: "#94a3b8", marginTop: 6 },
  mainGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)", gap: 16 },
  panel: { background: "#fff", padding: 20, borderRadius: 14, border: "1px solid #ede7f7", boxShadow: "0 2px 8px rgba(76,29,149,0.04)" },
  panelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10 },
  panelTitle: { fontSize: 15, fontWeight: 800, color: "#1c1230" },
  panelBadge: { background: "#f5f0ff", color: "#7B3F98", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 },
  empty: { padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13, background: "#faf7fd", borderRadius: 10 },
  visitRow: { display: "flex", alignItems: "center", gap: 12, padding: 12, background: "#faf7fd", borderRadius: 10, cursor: "pointer", border: "1px solid #ede7f7" },
  searchInput: { padding: "7px 12px", borderRadius: 8, border: "1.5px solid #ede7f7", fontSize: 13, outline: "none", width: 180 },
  manageBtn: { padding: "7px 12px", borderRadius: 8, border: "1.5px dashed #d8cdf2", background: "#fff",
    color: "#64327D", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  link: { border: "none", background: "transparent", color: "#7B3F98", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "9px 8px", fontSize: 11, fontWeight: 800, color: "#6b6483", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #ede7f7" },
  tr: { cursor: "pointer", transition: "background .1s" },
  td: { padding: "11px 8px", borderBottom: "1px solid #f5f1fa", color: "#1c1230" },
  chip: { padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 },
  insightBox: { background: "linear-gradient(135deg, #f5f0ff, #fce7f3)", padding: 16, borderRadius: 14, border: "1px solid #d8b4fe" },
  alertBox: { background: "linear-gradient(135deg, #fef2f2, #fff7ed)", padding: 16, borderRadius: 14, border: "1px solid #fecaca", marginBottom: 16 },
  staleRow: { display: "flex", alignItems: "center", gap: 10, padding: 10, background: "#fff", borderRadius: 10, border: "1px solid #fee2e2", flexWrap: "wrap" },
  reportKpis: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 },
  reportKpi: { background: "#faf7fd", padding: "12px 14px", borderRadius: 12, border: "1px solid #ede7f7" },
  reportGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 },
  pagBar: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "12px 4px 4px" },
  pagBtn: { padding: "6px 12px", borderRadius: 8, border: "1px solid #ede7f7", background: "#fff", fontSize: 12, fontWeight: 700, color: "#3F1F5C", cursor: "pointer" },
  rankBox: { background: "#fff", padding: 14, borderRadius: 12, border: "1px solid #ede7f7" },
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(15,10,40,0.55)", display: "flex", justifyContent: "flex-end", zIndex: 1000, backdropFilter: "blur(4px)" },
  drawer: { width: "100%", maxWidth: 520, background: "#fff", height: "100%", overflow: "auto", padding: 24, boxShadow: "-20px 0 60px rgba(0,0,0,0.2)" },
  drawerClose: { background: "none", border: "none", fontSize: 26, cursor: "pointer", color: "#6b6483", padding: 0, lineHeight: 1 },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e5e0f0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" },
  infoBox: { background: "#faf7fd", padding: 12, borderRadius: 10, marginTop: 6, fontSize: 13, color: "#3f2a6b", lineHeight: 1.6, border: "1px solid #ede7f7" },
  actionCard: { background: "#fff", padding: 14, borderRadius: 12, border: "1px solid #ede7f7", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" },
  actionCardHead: { fontSize: 13, fontWeight: 800, color: "#1c1230", marginBottom: 8 },
  btnAction: (color) => ({ padding: "9px 14px", background: color, color: "#fff", borderRadius: 8, border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer" }),
  convertBox: { padding: 16, background: "linear-gradient(135deg, #f5f0ff, #ecfdf5)", borderRadius: 12, border: "1px solid #d8b4fe" },
};
